import { Button } from '@/components/UI/Common/Admin/Button';
import { Badge } from '@/components/UI/Common/Admin/Badge';
import { Checkbox } from '@/components/UI/Common/Admin/Checkbox';
import { LoadingSpinner } from '@/components/UI/Common/Admin/LoadingSpinner';
import { PageLayout } from '@/components/UI/Layouts/Admin/PageLayout';
import { Pagination } from '@/components/UI/Pagination/Pagination';
import Table from '@/components/UI/Table/Table';
import { ROUTES } from '@/config/routes';
import { useResourceList } from '@/hooks/useResourceList';
import { useDeleteMany } from '@/queries/admin';
import { AdminChat, ChatGroup } from '@/types/chats';
import { TableColumn } from '@/types/table';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { Filters } from '../components/Filters';
import { IUserGroup } from '@/types/groups';

const ChatsAdminPage = () => {
  const {
    data: chats,
    isLoading,
    reset,
    handleFilterChange,
    handleSortChange,
    handlePageChange,
    handleLimitChange,
    filter,
    page,
    sortOrder,
    totalPages,
    totalItems,
    limit,
    sortBy,
  } = useResourceList('chats');
  const { mutateAsync: deleteChats } = useDeleteMany('chats');
  const [idsToDelete, setIdsToDelete] = useState<string[]>([]);
  const navigate = useNavigate();

  const toggleDelete = () => {
    if (idsToDelete.length === chats?.data.length) {
      setIdsToDelete([]);
    } else {
      setIdsToDelete(chats?.data.map((chat) => chat.id) || []);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteChats({ ids: idsToDelete });
      toast.success('Чаты успешно удалены');
      setIdsToDelete([]);
    } catch (error) {
      console.error('Error deleting chats:', error);
      toast.error('Произошла ошибка при удалении чатов');
    }
  };

  const renderCheckBox = (_val: unknown, row: AdminChat) => (
    <Checkbox
      className="prevent-row-click"
      checked={idsToDelete.includes(row.id)}
      onChange={() => {
        if (idsToDelete.includes(row.id)) {
          setIdsToDelete(idsToDelete.filter((id) => id !== row.id));
        } else {
          setIdsToDelete([...idsToDelete, row.id]);
        }
      }}
    />
  );

  const columns: TableColumn<AdminChat>[] = useMemo(
    () => [
      {
        key: '',
        title: (
          <Checkbox
            onClick={toggleDelete}
            onChange={() => {}}
            checked={idsToDelete.length > 0 && idsToDelete.length === chats?.data.length}
          />
        ),
        render: renderCheckBox,
      },
      {
        key: 'id',
        title: 'ID',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'title',
        title: 'Название',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'systemName',
        title: 'Системное имя',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'type',
        title: 'Тип',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      { key: 'userId', title: 'ID пользователя' },
      {
        key: 'sort',
        title: 'Порядок',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
      },
      {
        key: 'userGroups',
        title: 'Группы пользователей',
        render: (val: unknown) => {
          const groups = val as IUserGroup[];
          return groups?.length ? (
            <div className="flex flex-wrap gap-1">
              {groups.map((group) => (
                <Badge key={group.id} variant="info">
                  {group.name}
                </Badge>
              ))}
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        key: 'chatGroups',
        title: 'Группы чатов',
        render: (val: unknown) => {
          const groups = val as ChatGroup[];
          return groups?.length ? (
            <div className="flex flex-wrap gap-1">
              {groups.map((group) => (
                <Badge key={group.id} variant="default">
                  {group.title}
                </Badge>
              ))}
            </div>
          ) : (
            '-'
          );
        },
      },
      {
        key: 'createdAt',
        title: 'Создан',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
      {
        key: 'updatedAt',
        title: 'Обновлён',
        sortable: true,
        sortBy,
        sortOrder,
        onColumnHeaderClick: handleSortChange,
        render: (val: unknown) =>
          new Date(val as string).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    [chats?.data.length, idsToDelete, sortBy, sortOrder],
  );

  if (isLoading || !chats) {
    return <LoadingSpinner className="h-full" text="Загрузка..." />;
  }

  return (
    <PageLayout
      title="Чаты"
      actions={
        <div className="flex gap-4">
          {idsToDelete.length > 0 && (
            <Button onClick={handleDelete} size="md" variant="danger">
              Удалить
            </Button>
          )}
          <Link to={ROUTES.ADMIN.CREATE('chats')}>
            <Button size="md" variant="success">
              Создать
            </Button>
          </Link>
        </div>
      }
    >
      <Filters value={filter} onChange={handleFilterChange} onReset={reset} />

      <div className="overflow-x-auto">
        <Table
          data={chats.data}
          columns={columns}
          emptyMessage="Чаты не найдены"
          onRowClick={(row) => navigate(ROUTES.ADMIN.EDIT('chats', row.id))}
        />
      </div>

      <Pagination
        className="mt-10"
        onItemsPerPageChange={handleLimitChange}
        currentPage={page}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        itemsPerPage={limit}
      />
    </PageLayout>
  );
};

export default ChatsAdminPage;
